import { fetchPatientReturns } from '@/entities/patient-return';
import { DeviceInfoDrawer } from '@/entities/device';
import { closeIconV4 } from '@/shared/ui/assets';
import { PatientInfoDrawer, StudyInfoDrawer } from '@/entities/study';
import { CustomButton } from '@/shared/ui/components';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import TableWrapper from '../tableWrapper';
import MarkDeviceReturnedModal from '../markDeviceReturnedModal/markDeviceReturnedModal';
import { useWaitingToReturnTable } from './useWaitingToReturnTable';

/**
 * Table of devices waiting to be returned, with row selection, bulk mark-as-returned and drawers for device, study and patient details. Wires the state and callbacks from useWaitingToReturnTable into the confirm modal and the three drawers, shows a selection bar with the selected device count plus clear and mark actions while rows are selected, and loads rows through fetchPatientReturns with the current filter and paging cursor.
 *
 * @returns The waiting-to-return table markup with its modal and drawers.
 */
const WaitingToReturnTable = () => {
  const {
    searchValue,
    refTableComponent,
    state,
    filter,
    toggleDeviceInfoDrawer,
    toggleMarkDeviceReturnedModal,
    toggleStudyDrawer,
    togglePatientDrawer,
    onMarkDeviceReturned,
    generateColumns,
    onClickRemoveAllSelected,
    handleClickMarkAsReturned,
    onSort,
    getPagingCursor,
    rowSelection,
    isSelectingDevices,
  } = useWaitingToReturnTable();

  return (
    <div className="waiting-to-return-table">
      <MarkDeviceReturnedModal
        isOpen={state.isOpenMarkDeviceReturnedModal}
        data={state.markDeviceReturnedModalData}
        toggle={toggleMarkDeviceReturnedModal}
        onConfirm={onMarkDeviceReturned}
      />

      <DeviceInfoDrawer
        visible={state.isOpenDeviceInfoDrawer}
        deviceId={state.dataDeviceInfoDrawer}
        handleClickCloseButton={() => {
          toggleDeviceInfoDrawer();
        }}
      />
      <StudyInfoDrawer
        visible={state.isOpenStudyDrawer}
        fetchStudyId={state.dataStudyDrawer?.id}
        handleClickCloseButton={() => {
          toggleStudyDrawer();
        }}
      />
      <PatientInfoDrawer
        visible={state.isOpenPatientDrawer}
        title={t`Patient information`}
        studyId={state.dataPatientDrawer}
        handleClickCloseButton={() => {
          togglePatientDrawer();
        }}
      />

      {isSelectingDevices && (
        <div className="device-selected-label">
          <CustomButton
            type="tertiary"
            size="small"
            id="close-returned-selected-id-btn"
            icon={<ReactSVG src={closeIconV4} />}
            onClick={onClickRemoveAllSelected}
            ariaLabel={t`Clear selection`}
          />
          <div className="selected-devices">
            <span className="devices-number">{state.selectedRow.length}</span>
            {state.selectedRow.length > 1 ? ' devices selected' : ' device selected'}
          </div>
          <CustomButton
            size="small"
            type="tertiary"
            id="mask-as-returned-btn"
            onClick={handleClickMarkAsReturned}
          >
            {t`Mark as returned`}
          </CustomButton>
        </div>
      )}

      <TableWrapper
        {...({} as any)}
        ref={refTableComponent}
        className={classNames('waiting-to-return-table-wrapper', {
          'is-selecting-devices': isSelectingDevices,
        })}
        rowKey="id"
        rowSelection={rowSelection}
        generateColumns={generateColumns}
        getPagingCursor={getPagingCursor}
        fetchData={fetchPatientReturns}
        filter={filter}
        onSort={onSort}
        scrollX={1472}
        isSearching={searchValue}
      />
    </div>
  );
};

export default WaitingToReturnTable;
